// Generated by GitHub Copilot
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, X } from 'lucide-react'
import { FormSettingsEditor } from '#components/domain/operations/settings/form-settings-editor.tsx'
import { PublicWorkRequestSettings } from '#components/domain/operations/work-requests/settings/public-work-request-settings.tsx'
import { WorkRequestSettingsState } from '#components/domain/operations/work-requests/settings/work-request-settings-state.ts'
import { Tabs } from '#components/primitives/components/tabs.tsx'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import { useComponentState } from '#core/state/State.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useQuickSettingsReturn } from '#hooks/use-quick-settings-return.ts'

export const Route = createFileRoute('/_appSettings/$companyId/settings/work-requests')({
  component: RouteComponent,
})

/** Work request create form fields in order. Maps to the work-order create schema; name/email/files/subscribers are virtual fields the backend recognises. */
const WORK_REQUEST_FIELD_ORDER = [
  'title',
  'description',
  'asset_id',
  'files',
  'priority',
  'subscribers',
  'name',
  'email',
] as const

const WR_ALWAYS_VISIBLE = ['title']
const WR_ALWAYS_REQUIRED = ['title']

type WorkRequestSettingsTab = 'form' | 'public-access'

/** Inline warning shown when the Asset field is visible but public guests can't read assets. */
function AssetAccessWarning({ message }: { message: string }) {
  return (
    <div className="alert alert-warning d-flex align-items-start gap-2" role="alert">
      <AlertTriangle size={18} className="flex-shrink-0 mt-1" />
      <span>{message}</span>
    </div>
  )
}

/** Settings page for Work Requests — tabbed: form field settings and public-access configuration. */
function RouteComponent() {
  const { t } = useTranslation('settings')
  const { t: tGeneral } = useTranslation('general')
  const [activeTab, setActiveTab] = useState<WorkRequestSettingsTab>('form')
  const state = useComponentState(WorkRequestSettingsState)
  const userState = useUserState()
  const { showClose, handleClose } = useQuickSettingsReturn()

  // Per-tab permission gating: form config vs the public-access (company UPDATE) tab.
  const canEditForm = userState.hasPermission(PermissionService.WORK_ORDER_REQUESTS, PermissionAction.FORM_SETTINGS)
  const canManagePublic = userState.hasPermission(PermissionService.COMPANY, PermissionAction.UPDATE)

  const tabs = [
    canEditForm && { key: 'form' as const, label: t('workRequestsSettings.tabForm') },
    canManagePublic && { key: 'public-access' as const, label: t('workRequestsSettings.tabPublicAccess') },
  ].filter(Boolean) as { key: WorkRequestSettingsTab; label: string }[]

  // Fall back to the first permitted tab when the active one is hidden (e.g. a deep link).
  const effectiveTab = tabs.some((tb) => tb.key === activeTab) ? activeTab : tabs[0]?.key

  const fieldLabels: Record<string, string> = {
    title: t('shared.fields.title'),
    description: t('shared.fields.description'),
    asset_id: t('workRequestsSettings.fields.asset_id'),
    files: t('shared.fields.attachments'),
    priority: t('shared.fields.priority'),
    subscribers: t('shared.fields.subscribers'),
    name: t('shared.fields.name'),
    email: t('shared.fields.email'),
  }

  return (
    <div className="p-4 overflow-y-auto h-100">
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h1 className="h4 fw-bold mb-1">{t('workRequestsSettings.title')}</h1>
          <p className="text-secondary mb-0">{t('workRequestsSettings.description')}</p>
        </div>
        {showClose && (
          <button
            type="button"
            className="btn btn-sm btn-icon btn-outline-secondary border-0"
            onClick={handleClose}
            aria-label={t('shared.actions.close')}
          >
            <X size={20} />
          </button>
        )}
      </div>

      {tabs.length === 0 ? (
        <div className="text-secondary">
          <h2 className="h6 fw-bold mb-1">{tGeneral('notAuthorized.title')}</h2>
          <p className="mb-0">{tGeneral('notAuthorized.message')}</p>
        </div>
      ) : (
        <>
          <Tabs
            tabs={tabs}
            activeKey={effectiveTab}
            onChange={(key) => setActiveTab(key as WorkRequestSettingsTab)}
            variant="underline"
            size="sm"
            className="mb-4"
          />

          {effectiveTab === 'form' && (
            <>
              {state.assetAccessWarning && <AssetAccessWarning message={t('workRequestsSettings.assetWarning.form')} />}
              <FormSettingsEditor
                entityType="work_request"
                fieldLabels={fieldLabels}
                orderedFields={[...WORK_REQUEST_FIELD_ORDER]}
                alwaysVisibleFields={WR_ALWAYS_VISIBLE}
                alwaysRequiredFields={WR_ALWAYS_REQUIRED}
                onSettingsChange={(settings) => state.syncAssetFieldVisible(settings)}
                canEdit={canEditForm}
              />
            </>
          )}

          {effectiveTab === 'public-access' && (
            <>
              {state.assetAccessWarning && (
                <AssetAccessWarning message={t('workRequestsSettings.assetWarning.publicAccess')} />
              )}
              <PublicWorkRequestSettings state={state} />
            </>
          )}
        </>
      )}
    </div>
  )
}
